import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";

const PLANS = [
  {
    id: "freelance",
    amountKey: "billing.amount.freelance",
    cadenceKey: "billing.monthly",
    descriptionKey: "billing.plan.freelance.description",
    featureKeys: ["billing.one_project", "billing.core_workflow"],
  },
  {
    id: "business",
    amountKey: "billing.amount.business",
    cadenceKey: "billing.monthly",
    descriptionKey: "billing.plan.business.description",
    featureKeys: ["billing.five_projects", "billing.core_workflow", "billing.ai_coach"],
  },
  {
    id: "enterprise",
    amountKey: "billing.amount.enterprise",
    cadenceKey: "billing.monthly",
    descriptionKey: "billing.plan.enterprise.description",
    featureKeys: ["billing.unlimited_projects", "billing.core_workflow", "billing.ai_coach"],
  },
];

export default function BillingPage() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState("");
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [checkoutUrls, setCheckoutUrls] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(
    PLANS.some((plan) => plan.id === searchParams.get("plan")) ? searchParams.get("plan") : "freelance"
  );

  useEffect(() => {
    let active = true;
    api.get("/billing/lemon/status")
      .then(({ data }) => {
        if (active) setStatus(data);
      })
      .catch((requestError) => {
        if (active) setStatusError(formatApiError(requestError.response?.data?.detail));
      })
      .finally(() => {
        if (active) setLoadingStatus(false);
      });
    return () => { active = false; };
  }, []);

  async function startCheckout(plan) {
    setLoadingPlan(plan);
    setError("");
    try {
      const { data } = await api.post("/billing/lemon/checkout", { plan });
      if (data.test_mode) {
        setError(t("billing.test_redirect_warning"));
      }
      setCheckoutUrls((current) => ({ ...current, [plan]: data.checkout_url }));
      if (window.LemonSqueezy?.Url?.Open) {
        window.LemonSqueezy.Url.Open(data.checkout_url);
      } else {
        window.location.assign(data.checkout_url);
      }
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
      setLoadingPlan("");
    }
  }

  return (
    <main className="min-h-screen bg-[var(--background)] px-4 py-10 text-[var(--text)] sm:px-8">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8 flex items-center justify-between gap-4">
          <Link to="/#pricing" className="text-sm font-semibold text-[var(--text-2)] hover:text-[var(--text)]">
            ← {t("billing.back")}
          </Link>
          <span className="rounded-sm border border-[var(--border)] px-3 py-1 text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">
            Lemon Squeezy
          </span>
        </header>

        <section className="grid-card p-5 sm:p-8">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-2)]">{t("billing.kicker")}</p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">{t("billing.title")}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--text-2)]">{t("billing.intro")}</p>

          {status?.test_mode && (
            <div className="mt-5 border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-[var(--text)]" role="status">
              {t("billing.test_mode")}
            </div>
          )}
          {status?.billing_status && status.billing_status !== "unpaid" && (
            <div className="mt-4 border border-[var(--border)] px-4 py-3 text-sm" role="status">
              {t("billing.current_status")} <strong>{status.billing_status}</strong>
              {status.plan_tier && <> · {t(`billing.plan.${status.plan_tier}`)}</>}
            </div>
          )}
          {statusError && <p className="mt-4 text-sm text-red-400" role="alert">{statusError}</p>}
          {error && <p className="mt-4 text-sm text-red-400" role="alert">{error}</p>}

          {!loadingStatus && status && !Object.values(status.configured_plans || {}).some(Boolean) && !status.configured && (
            <p className="mt-5 border border-[var(--border)] px-4 py-3 text-sm text-[var(--text-2)]" role="status">
              {t("billing.not_configured")}
            </p>
          )}

          <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {PLANS.map((plan) => (
              <article
                key={plan.id}
                className={`rounded-sm border p-5 ${selectedPlan === plan.id ? "border-cyan-500/70 bg-cyan-500/5" : "border-[var(--border)]"}`}
                data-testid={`billing-plan-${plan.id}`}
              >
                <h2 className="text-xl font-bold">{t(`billing.plan.${plan.id}`)}</h2>
                <div className="mt-3 flex items-baseline gap-0">
                  <span className="text-3xl font-bold">{t(plan.amountKey)}</span>
                  <span className="text-sm text-[var(--text-2)]">{t(plan.cadenceKey)}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[var(--text-2)]">{t(plan.descriptionKey)}</p>
                <ul className="mt-4 space-y-2 text-sm text-[var(--text-2)]">
                  {plan.featureKeys.map((featureKey) => (
                    <li key={featureKey} className="flex gap-2">
                      <span aria-hidden="true" className="text-cyan-500">•</span>
                      <span>{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={checkoutUrls[plan.id] || "#billing-checkout"}
                  className={`btn-primary mt-5 flex w-full items-center justify-center px-4 py-2.5 text-sm font-semibold ${
                    checkoutUrls[plan.id] ? "lemonsqueezy-button" : ""
                  } ${
                    loadingStatus || !(status?.configured_plans?.[plan.id] ?? status?.configured) || Boolean(loadingPlan)
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                  aria-disabled={loadingStatus || !(status?.configured_plans?.[plan.id] ?? status?.configured) || Boolean(loadingPlan)}
                  tabIndex={loadingStatus || !(status?.configured_plans?.[plan.id] ?? status?.configured) || Boolean(loadingPlan) ? -1 : 0}
                  onClick={(event) => {
                    event.preventDefault();
                    if (loadingStatus || !(status?.configured_plans?.[plan.id] ?? status?.configured) || loadingPlan) return;
                    setSelectedPlan(plan.id);
                    startCheckout(plan.id);
                  }}
                  data-testid={`billing-checkout-${plan.id}`}
                >
                  {loadingPlan === plan.id ? t("billing.redirecting") : t("billing.continue")}
                </a>
              </article>
            ))}
          </div>

          <p className="mt-5 text-xs leading-relaxed text-[var(--text-2)]">{t("billing.webhook_note")}</p>
          <a href="https://nextregsolution.com/contacto" target="_blank" rel="noreferrer" className="mt-5 inline-block text-sm font-semibold text-cyan-400 hover:underline">
            {t("billing.enterprise")}
          </a>
        </section>
      </div>
    </main>
  );
}
